import { useCallback, useMemo } from 'react';
import { getAccessToken, decodeToken } from './auth';

/**
 * Generate a localStorage key for notification tracking
 * @param {string} action - The action type ('declined' or 'sent')
 * @param {string} userId - The user ID from JWT token
 * @param {string} bookId - The book/file ID
 * @returns {string} - The formatted localStorage key
 */
function getNotificationKey(action, userId, bookId) {
  return `dissona-notif-${action}-${userId}-${bookId}`;
}

/**
 * Extract user ID from JWT access token
 * @returns {string|null} - The user ID or null if not available
 */
function getUserIdFromToken() {
  try {
    const token = getAccessToken();
    if (!token) return null;

    const decoded = decodeToken(token);
    if (!decoded || !decoded.sub) return null;

    return decoded.sub;
  } catch (error) {
    console.error('Error extracting userId from token:', error);
    return null;
  }
}

/**
 * Check if browser supports the Notification API
 * @returns {boolean} - True if Notification API is available
 */
function isNotificationSupported() {
  return 'Notification' in window;
}

/**
 * Check the current notification permission status
 * @returns {string} - 'default', 'granted', or 'denied'
 */
function checkNotificationPermission() {
  if (!isNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Check if user has previously declined notifications for this book
 * @param {string} userId - The user ID
 * @param {string} bookId - The book/file ID
 * @returns {boolean} - True if user has declined
 */
function hasUserDeclined(userId, bookId) {
  if (!userId || !bookId) return false;

  try {
    const key = getNotificationKey('declined', userId, bookId);
    return localStorage.getItem(key) === 'true';
  } catch (error) {
    console.error('Error checking decline state:', error);
    return false;
  }
}

/**
 * Mark that user has declined notifications for this book
 * @param {string} userId - The user ID
 * @param {string} bookId - The book/file ID
 */
function markAsDeclined(userId, bookId) {
  if (!userId || !bookId) return;

  try {
    const key = getNotificationKey('declined', userId, bookId);
    localStorage.setItem(key, 'true');
  } catch (error) {
    console.error('Error marking as declined:', error);
  }
}

/**
 * Request notification permission from the browser
 * @param {string} userId - The user ID
 * @param {string} bookId - The book/file ID
 * @returns {Promise<string>} - The permission result ('granted', 'denied', or 'default')
 */
async function requestNotificationPermission(userId, bookId) {
  if (!isNotificationSupported()) {
    return 'denied';
  }

  // Don't request if already decided
  const currentPermission = Notification.permission;
  if (currentPermission !== 'default') {
    return currentPermission;
  }

  // Don't request if user previously declined for this book
  if (hasUserDeclined(userId, bookId)) {
    return 'denied';
  }

  try {
    const result = await Notification.requestPermission();

    // Store decline state if user denied
    if (result === 'denied') {
      markAsDeclined(userId, bookId);
    }

    return result;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
}

/**
 * Check if a notification has already been sent for this book
 * @param {string} userId - The user ID
 * @param {string} bookId - The book/file ID
 * @returns {boolean} - True if notification was already sent
 */
function hasNotificationBeenSent(userId, bookId) {
  if (!userId || !bookId) return false;

  try {
    const key = getNotificationKey('sent', userId, bookId);
    return localStorage.getItem(key) === 'true';
  } catch (error) {
    console.error('Error checking sent state:', error);
    return false;
  }
}

/**
 * Mark that a notification has been sent for this book
 * @param {string} userId - The user ID
 * @param {string} bookId - The book/file ID
 */
function markNotificationSent(userId, bookId) {
  if (!userId || !bookId) return;

  try {
    const key = getNotificationKey('sent', userId, bookId);
    localStorage.setItem(key, 'true');
  } catch (error) {
    console.error('Error marking notification as sent:', error);
  }
}

/**
 * Check if we can send a notification (permission granted and not already sent)
 * @param {string} userId - The user ID
 * @param {string} bookId - The book/file ID
 * @returns {boolean} - True if notification can be sent
 */
function canSendNotification(userId, bookId) {
  if (!isNotificationSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  if (hasNotificationBeenSent(userId, bookId)) return false;
  return true;
}

/**
 * Send a notification that the first chapter is ready
 * @param {string} bookTitle - The title of the book
 * @param {string} bookId - The book/file ID
 * @param {string} userId - The user ID
 * @returns {Notification|null} - The notification object or null if failed
 */
function sendFirstChapterNotification(bookTitle, bookId, userId) {
  if (!canSendNotification(userId, bookId)) {
    return null;
  }

  try {
    const notification = new Notification('Dissona', {
      body: `${bookTitle} - First chapter ready to listen`,
      icon: '/favicon-192x192.png',
      tag: `first-chapter-${bookId}`,
      requireInteraction: false,
    });

    // Add click handler to navigate to chapters page
    notification.onclick = function() {
      window.focus();
      window.location.href = `/projects/${bookId}/chapters`;
      notification.close();
    };

    // Mark as sent after successful creation
    markNotificationSent(userId, bookId);

    return notification;
  } catch (error) {
    console.error('Error creating notification:', error);
    return null;
  }
}

/**
 * Custom hook for managing browser notifications
 * @param {string} bookId - The current book/file ID
 * @returns {object} - Hook functions and state
 */
export function useNotifications(bookId) {
  // Get userId from token
  const userId = useMemo(() => getUserIdFromToken(), []);

  // Check permission on mount
  const checkPermission = useCallback(() => {
    return checkNotificationPermission();
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    if (!userId || !bookId) return 'denied';
    return await requestNotificationPermission(userId, bookId);
  }, [userId, bookId]);

  // Send notification
  const sendNotification = useCallback((bookTitle) => {
    if (!userId || !bookId) return null;
    return sendFirstChapterNotification(bookTitle, bookId, userId);
  }, [userId, bookId]);

  // Check if already sent
  const alreadySent = useMemo(() => {
    if (!userId || !bookId) return false;
    return hasNotificationBeenSent(userId, bookId);
  }, [userId, bookId]);

  return {
    isSupported: isNotificationSupported(),
    checkPermission,
    requestPermission,
    sendNotification,
    alreadySent,
  };
}

export default useNotifications;
